import { useState, useEffect } from 'react';
import { StreamChat, Channel as StreamChatChannel } from 'stream-chat';
import { STREAM_API_KEY } from '@api/constants';

interface UseEventChatReturn {
  chatClient: StreamChat | null;
  channel: StreamChatChannel | null;
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useEventChat(chatChannelId: string | undefined): UseEventChatReturn {
  const [chatClient, setChatClient] = useState<StreamChat | null>(null);
  const [channel, setChannel] = useState<StreamChatChannel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!chatChannelId) {
      setIsLoading(false);
      return;
    }

    let mounted = true;
    let client: StreamChat | null = null;
    let shouldCleanup = false;

    const initChat = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Create new StreamChat instance (not singleton to avoid StrictMode conflicts)
        client = new StreamChat(STREAM_API_KEY, {
          timeout: 10000,
          enableWSFallback: true,
          allowServerSideConnect: true,
        });
        shouldCleanup = true;

        // Connect as anonymous user (read-only, no MAU cost)
        await client.connectAnonymousUser();

        // Join event lobby channel (anonymous users can read livestream channels)
        const eventChannel = client.channel('livestream', chatChannelId);
        await eventChannel.watch();

        // Update state if still mounted
        if (mounted) {
          setChatClient(client);
          setChannel(eventChannel);
          setIsLoading(false);
        } else {
          // Component unmounted during init - clean up
          await client.disconnectUser().catch(() => {
            // Ignore disconnect errors for anonymous users
          });
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to connect to chat');
          setIsLoading(false);
        }
      }
    };

    initChat();

    return () => {
      mounted = false;
      if (shouldCleanup && client) {
        client.disconnectUser().catch(() => {
          // Ignore disconnect errors for anonymous users
        });
      }
    };
  }, [chatChannelId]);

  return {
    chatClient,
    channel,
    isReady: !isLoading && !!channel && !error,
    isLoading,
    error,
  };
}
